// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
export const ROOT = '/';
export const HOME = '/';
export const GRAPHQL = '/graphql';
export const CLIENT = '/client';
export const HEALTH_CHECK = '/healthcheck';
export const LIVENESS_CHECK = '/livenesscheck';
export const PUBLIC = '/public';
export const HOOKS = '/hooks';

export const makePublic = (path: string) => `${PUBLIC}${path.startsWith('/') ? '' : '/'}${path}`;
export const makeRoot = (path: string) => path;

export const ERROR = '/error';
export const REPORT = '/report';
export const TRACEKIT = '/tracekit';
export const LOGIN = '/login';
export const LOGOUT = '/logout';
export const SIGNUP = '/signup';
export const SIGNIN = '/signin';
export const VERIFY_EMAIL = '/verify-email';
export const FEEDBACK = '/feedback';
export const SIGNUP_LINK = '/signup-link';
export const PASSWORD_RESET = '/reset-password';
export const FORGOT_PASSWORD = '/forgot-password';
export const METRICS = '/metrics'; // Metrics reverse proxy
export const FACEBOOK_CONNECT = '/facebook-connect'; // Facebook Connect reverse proxy
export const FACEBOOK_COM = '/facebook-com'; // Facebook reverse proxy
export const POLAR_WEBHOOK = '/polar-webhook';

export const SIGNIN_REDIRECT_PARAM = 'returnUrl';
export const makeSignInWithRedirect = (redirectTo: string) =>
  `${SIGNIN}?${SIGNIN_REDIRECT_PARAM}=${encodeURIComponent(redirectTo)}`;

export const VERIFY_CHECKOUT = '/verify-checkout';
export const isVerifyCheckout = (path: string) => path.startsWith(VERIFY_CHECKOUT);

export const isHome = (path: string) => path === HOME;

export const PRICING = '/pricing';
export const isPricing = (path: string) => path.startsWith('/pricing');

export const TUTORIALS = '/tutorials';
export const isTUTORIALS = (path: string) => path.startsWith('/tutorials');
export const PRICE_CALCULATOR_VIDEO = 'Microgreen Pricing & Profit Calculator 🌱';
export const makeVideoPath = (path: string) => `${TUTORIALS}?video=${encodeURIComponent(path)}`;

export const DASHBOARD = '/dashboard';
export const isDashboard = (path: string) => path.startsWith('/dashboard');

export const TEAM = '/team';
export const isTeam = (path: string) => path.startsWith('/team');

export const PRIVACY = '/privacy';
export const isPrivacy = (path: string) => path.startsWith('/privacy');

export const TERMS = '/terms';
export const isTerms = (path: string) => path.startsWith('/terms');

export const CONTACT = '/contact';
export const isContact = (path: string) => path.startsWith('/contact');

export const TRAY_CALCULATOR = '/tray-calculator';
export const isTrayCalculator = (path: string) => path.startsWith('/tray-calculator');

export const PRICE_CALCULATOR = '/price-calculator';
export const isPriceCalculator = (path: string) => path.startsWith('/price-calculator');

export const SEED_DENSITY_CALCULATOR = '/seed-density-calculator';
export const isSeedDensityCalculator = (path: string) => path.startsWith('/seed-density-calculator');

export const EXCEL_TOOL = '/excel-tool';
export const isExcelTool = (path: string) => path.startsWith('/excel-tool');

// Farm Report
export const FARM_REPORT = '/farm-report';
export const isFarmReport = (path: string) => path.startsWith('/farm-report');

// Calendar
export const CALENDAR = '/calendar';
export const isCalendar = (path: string) => path.startsWith('/calendar');

// Account
export const ACCOUNT = '/account';
export const isAccount = (path: string) => path.startsWith('/account');

// Crop
export const CROP_ID_ARGUMENT = 'cropId';
const CROP_ID_PARAM = `:${CROP_ID_ARGUMENT}`;
export const makeCropIdPath = (cropId: string = CROP_ID_PARAM) => `${CROP}/${cropId}`;
export const CROP = '/crop';
export const isCrop = (path: string) => path.startsWith('/crop') || path.startsWith(`${DASHBOARD}/crop`);

// Crops
export const CROPS = '/crops';
export const isCrops = (path: string) => path.startsWith('/crops') || path.startsWith(`${DASHBOARD}/crops`);
export const CROP_SEARCH_PAGE_ARGUMENT = 'page';
export const CROP_SEARCH_PAGE_PARAM = `:${CROP_SEARCH_PAGE_ARGUMENT}`;
export const makeCropSearch = (id: string | number = CROP_SEARCH_PAGE_PARAM) => `/browse/crop/${id}`;

// Planting
export const PLANTING_ID_ARGUMENT = 'plantingId';
const PLANTING_ID_PARAM = `:${PLANTING_ID_ARGUMENT}`;
export const makePlantingIdPath = (plantingId: string = PLANTING_ID_PARAM) => `${PLANTING}/${plantingId}`;
export const PLANTING = '/planting';
export const isPlanting = (path: string) => path.startsWith('/planting') || path.startsWith(`${DASHBOARD}/planting`);

// Planting Groups
export const PLANTINGS = '/plantings';
export const isPlantings = (path: string) => path.startsWith('/plantings') || path.startsWith(`${DASHBOARD}/plantings`);
export const PLANTING_SEARCH_PAGE_ARGUMENT = 'page';
export const PLANTING_SEARCH_PAGE_PARAM = `:${PLANTING_SEARCH_PAGE_ARGUMENT}`;
export const makePlantingSearch = (id: string | number = PLANTING_SEARCH_PAGE_PARAM) => `/browse/planting/${id}`;

// Harvest
export const HARVEST_ID_ARGUMENT = 'harvestId';
const HARVEST_ID_PARAM = `:${HARVEST_ID_ARGUMENT}`;
export const makeHarvestIdPath = (harvestId: string = HARVEST_ID_PARAM) => `${HARVEST}/${harvestId}`;
export const HARVEST = '/harvest';
export const isHarvest = (path: string) => path.startsWith('/harvest') || path.startsWith(`${DASHBOARD}/harvest`);

// Harvests
export const HARVESTS = '/harvests';
export const isHarvests = (path: string) => path.startsWith('/harvests') || path.startsWith(`${DASHBOARD}/harvests`);
export const HARVEST_SEARCH_PAGE_ARGUMENT = 'page';
export const HARVEST_SEARCH_PAGE_PARAM = `:${HARVEST_SEARCH_PAGE_ARGUMENT}`;
export const makeHarvestSearch = (id: string | number = HARVEST_SEARCH_PAGE_PARAM) => `/browse/harvest/${id}`;

// Task
export const TASK_ID_ARGUMENT = 'taskId';
const TASK_ID_PARAM = `:${TASK_ID_ARGUMENT}`;
export const makeTaskIdPath = (taskId: string = TASK_ID_PARAM) => `${TASK}/${taskId}`;
export const TASK = '/task';
export const isTask = (path: string) => path.startsWith('/task') || path.startsWith(`${DASHBOARD}/task`);

// Task Group
export const makeTaskGroupIdPath = (taskId: string = TASK_ID_PARAM) => `${TASK_GROUP}/${taskId}`;
export const TASK_GROUP = `${TASK}/group`;
export const isTaskGroup = (path: string) =>
  path.startsWith(TASK_GROUP) || path.startsWith(`${DASHBOARD}/${TASK_GROUP}`);

// Planting Group
export const makePlantingGroupIdPath = (plantingId: string = PLANTING_ID_PARAM) => `${PLANTING_GROUP}/${plantingId}`;
export const PLANTING_GROUP = `${PLANTING}/group`;
export const isPlantingGroup = (path: string) =>
  path.startsWith(PLANTING_GROUP) || path.startsWith(`${DASHBOARD}/${PLANTING_GROUP}`);

// Tasks
export const TASKS = '/tasks';
export const isTasks = (path: string) => path.startsWith('/tasks') || path.startsWith(`${DASHBOARD}/tasks`);
export const TASK_SEARCH_PAGE_ARGUMENT = 'page';
export const TASK_SEARCH_PAGE_PARAM = `:${TASK_SEARCH_PAGE_ARGUMENT}`;
export const makeTaskSearch = (id: string | number = TASK_SEARCH_PAGE_PARAM) => `/browse/task/${id}`;

// Recurring Task
export const RECURRING_TASK_ID_ARGUMENT = 'recurringTaskId';
const RECURRING_TASK_ID_PARAM = `:${RECURRING_TASK_ID_ARGUMENT}`;
export const makeRecurringTaskIdPath = (recurringTaskId: string = RECURRING_TASK_ID_PARAM) =>
  `${RECURRING_TASK}/${recurringTaskId}`;
export const RECURRING_TASK = '/recurring-task';
export const isRecurringTask = (path: string) =>
  path.startsWith('/recurring-task') || path.startsWith(`${DASHBOARD}/recurring-task`);

// Recurring Tasks
export const RECURRING_TASKS = '/recurring-tasks';
export const isRecurringTasks = (path: string) =>
  path.startsWith('/recurring-tasks') || path.startsWith(`${DASHBOARD}/recurring-tasks`);
export const RECURRING_TASK_SEARCH_PAGE_ARGUMENT = 'page';
export const RECURRING_TASK_SEARCH_PAGE_PARAM = `:${RECURRING_TASK_SEARCH_PAGE_ARGUMENT}`;
export const makeRecurringTaskSearch = (id: string | number = RECURRING_TASK_SEARCH_PAGE_PARAM) =>
  `/browse/recurring-task/${id}`;

// Recurring Planting
export const RECURRING_PLANTING_ID_ARGUMENT = 'recurringPlantingId';
const RECURRING_PLANTING_ID_PARAM = `:${RECURRING_PLANTING_ID_ARGUMENT}`;
export const makeRecurringPlantingIdPath = (recurringPlantingId: string = RECURRING_PLANTING_ID_PARAM) =>
  `${RECURRING_PLANTING}/${recurringPlantingId}`;
export const RECURRING_PLANTING = '/recurring-planting';
export const isRecurringPlanting = (path: string) =>
  path.startsWith('/recurring-planting') || path.startsWith(`${DASHBOARD}/recurring-planting`);

// Recurring Plantings
export const RECURRING_PLANTINGS = '/recurring-plantings';
export const isRecurringPlantings = (path: string) =>
  path.startsWith('/recurring-plantings') || path.startsWith(`${DASHBOARD}/recurring-plantings`);
export const RECURRING_PLANTING_SEARCH_PAGE_ARGUMENT = 'page';
export const RECURRING_PLANTING_SEARCH_PAGE_PARAM = `:${RECURRING_PLANTING_SEARCH_PAGE_ARGUMENT}`;
export const makeRecurringPlantingSearch = (id: string | number = RECURRING_PLANTING_SEARCH_PAGE_PARAM) =>
  `/browse/recurring-planting/${id}`;

// Order
export const ORDER_ID_ARGUMENT = 'orderId';
const ORDER_ID_PARAM = `:${ORDER_ID_ARGUMENT}`;
export const makeOrderIdPath = (orderId: string = ORDER_ID_PARAM) => `${ORDER}/${orderId}`;
export const ORDER = '/order';
export const isOrder = (path: string) => path.startsWith('/order') || path.startsWith(`${DASHBOARD}/order`);

// Orders
export const ORDERS = '/orders';
export const isOrders = (path: string) => path.startsWith('/orders') || path.startsWith(`${DASHBOARD}/orders`);
export const ORDER_SEARCH_PAGE_ARGUMENT = 'page';
export const ORDER_SEARCH_PAGE_PARAM = `:${ORDER_SEARCH_PAGE_ARGUMENT}`;
export const makeOrderSearch = (id: string | number = ORDER_SEARCH_PAGE_PARAM) => `/browse/order/${id}`;

// Recurring Order
export const RECURRING_ORDER_ID_ARGUMENT = 'recurringOrderId';
const RECURRING_ORDER_ID_PARAM = `:${RECURRING_ORDER_ID_ARGUMENT}`;
export const makeRecurringOrderIdPath = (recurringOrderId: string = RECURRING_ORDER_ID_PARAM) =>
  `${RECURRING_ORDER}/${recurringOrderId}`;
export const RECURRING_ORDER = '/recurring-order';
export const isRecurringOrder = (path: string) =>
  path.startsWith('/recurring-order') || path.startsWith(`${DASHBOARD}/recurring-order`);

// Recurring Orders
export const RECURRING_ORDERS = '/recurring-orders';
export const isRecurringOrders = (path: string) =>
  path.startsWith('/recurring-orders') || path.startsWith(`${DASHBOARD}/recurring-orders`);
export const RECURRING_ORDER_SEARCH_PAGE_ARGUMENT = 'page';
export const RECURRING_ORDER_SEARCH_PAGE_PARAM = `:${RECURRING_ORDER_SEARCH_PAGE_ARGUMENT}`;
export const makeRecurringOrderSearch = (id: string | number = RECURRING_ORDER_SEARCH_PAGE_PARAM) =>
  `/browse/recurring-order/${id}`;

// Blend
export const BLEND_ID_ARGUMENT = 'blendId';
const BLEND_ID_PARAM = `:${BLEND_ID_ARGUMENT}`;
export const makeBlendIdPath = (blendId: string = BLEND_ID_PARAM) => `${BLEND}/${blendId}`;
export const BLEND = '/blend';
export const isBlend = (path: string) => path.startsWith('/blend') || path.startsWith(`${DASHBOARD}/blend`);

// Blends
export const BLENDS = '/blends';
export const isBlends = (path: string) => path.startsWith('/blends') || path.startsWith(`${DASHBOARD}/blends`);
export const BLEND_SEARCH_PAGE_ARGUMENT = 'page';
export const BLEND_SEARCH_PAGE_PARAM = `:${BLEND_SEARCH_PAGE_ARGUMENT}`;
export const makeBlendSearch = (id: string | number = BLEND_SEARCH_PAGE_PARAM) => `/browse/blend/${id}`;

// Surpluses and Buffers
export const SURPLUSES_BUFFERS = '/surpluses-buffers';
export const isSurplusesBuffers = (path: string) =>
  path.startsWith('/surpluses-buffers') || path.startsWith(`${DASHBOARD}/surpluses-buffers`);
export const SURPLUSES_BUFFERS_SEARCH_PAGE_ARGUMENT = 'page';
export const SURPLUSES_BUFFERS_SEARCH_PAGE_PARAM = `:${SURPLUSES_BUFFERS_SEARCH_PAGE_ARGUMENT}`;
export const makeSurplusesBuffersSearch = (id: string | number = SURPLUSES_BUFFERS_SEARCH_PAGE_PARAM) =>
  `/browse/surpluses-buffers/${id}`;

// Blog
export const BLOG = '/blog';
export const isBlog = (path: string) => path.startsWith('/blog');
export const BLOG_SLUG_ARGUMENT = 'slug';
const BLOG_SLUG_PARAM = `:${BLOG_SLUG_ARGUMENT}`;
export const makeBlogTitlePath = (slug: string = BLOG_SLUG_PARAM) => `${BLOG}/${slug}`;

export const requiresAuth = (path: string) =>
  isDashboard(path) ||
  isCrop(path) ||
  isCrops(path) ||
  isOrder(path) ||
  isOrders(path) ||
  isBlend(path) ||
  isBlends(path) ||
  isTask(path) ||
  isTasks(path) ||
  isTaskGroup(path) ||
  isHarvest(path) ||
  isHarvests(path) ||
  isPlanting(path) ||
  isPlantings(path) ||
  isPlantingGroup(path) ||
  isRecurringOrder(path) ||
  isRecurringOrders(path) ||
  isRecurringPlanting(path) ||
  isRecurringPlantings(path) ||
  isRecurringTask(path) ||
  isRecurringTasks(path) ||
  isSurplusesBuffers(path) ||
  isAccount(path) ||
  isFarmReport(path) ||
  isCalendar(path);

export const isFreeTool = (path: string): boolean =>
  path === TRAY_CALCULATOR || path === PRICE_CALCULATOR || path === SEED_DENSITY_CALCULATOR || path === EXCEL_TOOL;
